import { HttpInterceptorFn, withInterceptors } from '@angular/common/http';
import { ENVIRONMENT_INITIALIZER, Provider, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LoginManager } from '@flyfreely-portal-ui/flyfreely';
import { filter } from 'rxjs';
import {
    AuthService,
    authInterceptor as _smsAuthInterceptor
} from 'sms-dashboard';

/**
 * The SMS authInterceptor re-exported and cast to the portal's HttpInterceptorFn.
 * The function is structurally identical between Angular 17 and 18; the cast resolves
 * a nominal type-incompatibility caused by two separate @angular/common/http packages
 * on disk (SMS lib vs portal node_modules).
 */
export const smsAuthInterceptor =
    _smsAuthInterceptor as unknown as HttpInterceptorFn;

/**
 * Returns the provider configuration for the SMS safety-assessment integration.
 *
 * Usage in the root app module providers:
 *
 *   providers: [
 *     ...provideSmsIntegration(),
 *   ]
 *
 * You must also add `withInterceptors([smsAuthInterceptor])` to the root
 * `provideHttpClient(...)` call so that the SMS bearer-token interceptor is
 * part of the shared HttpClient pipeline:
 *
 *   provideHttpClient(
 *     withInterceptorsFromDi(),
 *     withInterceptors([smsAuthInterceptor])
 *   )
 *
 * The interceptor only attaches tokens to requests targeting SMS_API_URL
 * so it is safe to place it in the root interceptor chain.
 */
export function provideSmsIntegration(): Provider[] {
    return [
        {
            provide: ENVIRONMENT_INITIALIZER,
            multi: true,
            useValue: () => {
                const smsAuth = inject(AuthService);

                inject(LoginManager)
                    .bearerToken$.pipe(
                        filter((token): token is string => !!token),
                        takeUntilDestroyed()
                    )
                    .subscribe(token =>
                        smsAuth.setUserContext({ accessToken: token })
                    );
            }
        }
    ];
}

export { withInterceptors };
